import axios from 'axios'
import { authService } from './authService'
import { createResponseErrorHandler } from './apiInterceptor'
import type { SegmentOverviewResponse, SegmentTopModelsResponse } from '../types/segments'

// Get API base URL from environment variable
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL

// Fail fast if environment variable is not set
if (!API_BASE_URL) {
  const errorMsg = 'FATAL: VITE_API_BASE_URL environment variable is not set. This must be configured in your deployment environment (Railway, etc.) before building the application.'
  console.error(errorMsg)
  console.error('Available env vars:', import.meta.env)
  throw new Error(errorMsg)
}

console.log('API Base URL configured:', API_BASE_URL)

// Create axios instance
// A request that never returns still holds its server-side DB connection, and
// the backend pool is 15 across a single uvicorn worker. On 2026-09-15 a
// bruktbil query took 270 s while the pool sat exhausted and logins 500'd.
// 60 s is above the slowest legitimate bruktbil aggregate (~18 s observed) and
// well under the point where a user has already given up and reloaded.
export const REQUEST_TIMEOUT_MS = 60_000

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: REQUEST_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
  },
  // withCredentials: true, // Disabled for Railway - causes CORS issues across domains
  paramsSerializer: { indexes: null }, // serialize arrays as ?classes=A&classes=B (FastAPI style)
})

// Request interceptor to add JWT token
api.interceptors.request.use(
  (config) => {
    // Try JWT token first
    const accessToken = authService.getAccessToken()
    if (accessToken) {
      config.headers['Authorization'] = `Bearer ${accessToken}`
    } else {
      // Fallback to API key for backward compatibility
      const apiKey = localStorage.getItem('apiKey')
      if (apiKey) {
        config.headers['X-API-Key'] = apiKey
      }
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor for error handling and token refresh
// Shared handler: on 401, always attempt cookie-based refresh (works whether
// the in-memory access token is present or already cleared after page refresh).
api.interceptors.response.use(
  (response) => response,
  createResponseErrorHandler(authService, (cfg) => api(cfg)),
)

// ---- Organization details (Næring aktør detaljer) types ----
export type OrgRole =
  | "kjopt" | "leaset_inn" | "leaset_ut_naering" | "leaset_ut_privat" | "leaset_ut_udefinert"

export interface OrgDetailsRoleBucket {
  total: number
  kjopt: number
  leaset_inn: number
  leaset_ut_naering: number
  leaset_ut_privat: number
  leaset_ut_udefinert: number
}

export interface OrgDetailsSummary {
  org_number: string
  org_name: string
  classification: string
  selected: OrgDetailsRoleBucket
  rest_of_2025_2026: OrgDetailsRoleBucket
  selected_share_percent: number
}

export interface OrgDetailsTimelineWeek {
  iso_year: number
  iso_week: number
  kjopt: number
  leaset_inn: number
  leaset_ut_naering: number
  leaset_ut_privat: number
  leaset_ut_udefinert: number
}

export interface OrgDetailsAggregatedRow {
  reg_date: string
  brand: string
  master_model: string | null
  variant: string | null
  type: OrgRole
  antall: number
}

export interface OrgDetailsAggregatedResponse {
  data: OrgDetailsAggregatedRow[]
  pagination: { page: number; page_size: number; total: number }
}

// -- Age Analytics: Master Model types --

export interface AvgAgeByMasterModelRow {
  master_model_id: number
  master_model_name: string
  brand: string
  current_count: number
  current_avg_age: number | null
  previous_count: number
  previous_avg_age: number | null
}

export interface AvgAgeByMasterModelResponse {
  output_mode: string
  category: string
  periods: any
  total_rows: number
  data: AvgAgeByMasterModelRow[]
}

export interface MasterModelDistData {
  master_model_id: number
  master_model_name: string
  brand: string
  category: string
  total_count: number
  average_age: number | null
  median_age: number | null
  distribution: Array<{ age: number; count: number; percentage: number }>
}

export interface MasterModelDistResponse {
  primary: MasterModelDistData
  compare: MasterModelDistData | null
  periods: any | null
}

// Public Endpoints (no API key required)
export interface AndreKjoretoyParams {
  gruppe: string
  output_mode: string
  type: 'ny' | 'bruktimport' | 'alle'
  aar?: number
  mine_postnummer?: string
  custom_start_date?: string
  custom_end_date?: string
  limit?: number
}

export const publicAPI = {
  // Top brands
  getTopBrands: (params?: {
    start_date?: string
    end_date?: string
    limit?: number
    mine_postnummer?: string
    varebil?: boolean
  }) => api.get('/public/top-brands', { params }),

  // Top brands comparison (YoY)
  getTopBrandsComparison: (params: {
    start_date: string
    end_date: string
    prev_start_date: string
    prev_end_date: string
    limit?: number
  }) => api.get('/public/top-brands-comparison', { params }),

  // Top model variants
  getTopModelVariants: (params?: {
    start_date?: string
    end_date?: string
    limit?: number
    mine_postnummer?: string
  }) => api.get('/public/top-model-variants', { params }),

  // Top model variants comparison (YoY)
  getTopModelVariantsComparison: (params: {
    start_date: string
    end_date: string
    prev_start_date: string
    prev_end_date: string
    limit?: number
    mine_postnummer?: string
  }) => api.get('/public/top-model-variants-comparison', { params }),

  // Brand model details
  getBrandModels: (brandName: string, params?: {
    start_date?: string
    end_date?: string
    limit?: number
  }) => api.get(`/public/brands/${encodeURIComponent(brandName)}/models`, { params }),

  // Registration counts for multiple periods
  getRegistrationCounts: (params?: {
    reference_date?: string
    custom_start_date?: string
    custom_end_date?: string
  }) => api.get('/public/registration-counts', { params }),

  // Summary statistics
  getSummary: (params?: {
    start_date?: string
    end_date?: string
    varebil?: boolean
  }) => api.get('/public/summary', { params }),

  // Leasing statistics
  getLeasingStatistics: (params: {
    start_date: string
    end_date: string
    varebil?: boolean
  }) => api.get('/public/leasing-statistics', { params }),

  // Ownership statistics
  getOwnershipStatistics: (params: {
    start_date: string
    end_date: string
    varebil?: boolean
  }) => api.get('/public/ownership-statistics', { params }),

  // Leasing type statistics
  getLeasingTypeStatistics: (params: {
    start_date: string
    end_date: string
  }) => api.get('/public/leasing-type-statistics', { params }),

  // Geographic Analytics
  getCountiesList: () => api.get('/public/geographic/counties/list'),
  
  getMunicipalitiesList: (params?: {
    county?: string
  }) => api.get('/public/geographic/municipalities/list', { params }),
  
  getCountiesSales: (params?: {
    start_date?: string
    end_date?: string
    limit?: number
  }) => api.get('/public/geographic/counties/sales', { params }),
  
  getCountiesComparison: (params: {
    county_names: string
    start_date?: string
    end_date?: string
  }) => api.get('/public/geographic/counties/comparison', { params }),
  
  getMunicipalitiesSales: (params?: {
    county?: string
    start_date?: string
    end_date?: string
    limit?: number
  }) => api.get('/public/geographic/municipalities/sales', { params }),
  
  getBrandsByLocation: (params: {
    location_type: 'county' | 'municipality'
    location_name: string
    start_date?: string
    end_date?: string
    limit?: number
  }) => api.get('/public/geographic/brands-by-location', { params }),
  
  getPostalCodeDetails: (postalCode: string, params?: {
    start_date?: string
    end_date?: string
  }) => api.get(`/public/geographic/postal-codes/${postalCode}`, { params }),

  getCountyYoY: (params: {
    current_start: string
    current_end: string
    previous_start: string
    previous_end: string
    mine_postnummer?: string
    varebil?: boolean
  }) => api.get('/public/geographic/counties/yoy', { params }),

  getMunicipalityYoY: (params: {
    current_start: string
    current_end: string
    previous_start: string
    previous_end: string
    county?: string
    mine_postnummer?: string
    varebil?: boolean
    limit?: number
  }) => api.get('/public/geographic/municipalities/yoy', { params }),

  // Organization Analytics
  getOrganizationsList: (params?: {
    business_area?: string
    min_vehicles?: number
  }) => api.get('/public/organizations/list', { params }),
  
  getOrganizationMarketShare: (params?: {
    start_date?: string
    end_date?: string
    limit?: number
    page?: number
    page_size?: number
    mine_postnummer?: string
    varebil?: boolean
    classification?: string
    naering_filter?: string
    search?: string
  }) => api.get('/public/organizations/market-share', { params }),
  
  getOrganizationClassifications: (params?: {
    start_date?: string
    end_date?: string
    mine_postnummer?: string
    varebil?: boolean
  }) => api.get('/public/organization-classifications', { params }),
  
  getOrganizationFleetDetails: (orgNumber: string, params?: {
    start_date?: string
    end_date?: string
  }) => api.get(`/public/organizations/${orgNumber}/fleet-details`, { params }),
  
  getOrganizationComparison: (params: {
    org_numbers: string
    start_date?: string
    end_date?: string
  }) => api.get('/public/organizations/comparison', { params }),

  // Segment Analytics (new typed helpers — Task 12)
  getSegmentsOverview: async (params: {
    start_date: string
    end_date: string
    mine_postnummer?: string
    varebil?: boolean
  }): Promise<SegmentOverviewResponse> => {
    const qp: Record<string, string> = {
      start_date: params.start_date,
      end_date: params.end_date,
    }
    if (params.mine_postnummer !== undefined) qp.mine_postnummer = params.mine_postnummer
    if (params.varebil !== undefined) qp.varebil = params.varebil ? 'true' : 'false'
    const res = await api.get<SegmentOverviewResponse>('/public/segments/overview', { params: qp })
    return res.data
  },

  getSegmentTopModelsTyped: async (params: {
    segment_id: number
    start_date: string
    end_date: string
    mine_postnummer?: string
    varebil?: boolean
    limit?: number
  }): Promise<SegmentTopModelsResponse> => {
    const qp: Record<string, string | number> = {
      segment_id: params.segment_id,
      start_date: params.start_date,
      end_date: params.end_date,
    }
    if (params.mine_postnummer !== undefined) qp.mine_postnummer = params.mine_postnummer
    if (params.varebil !== undefined) qp.varebil = params.varebil ? 'true' : 'false'
    if (params.limit !== undefined) qp.limit = params.limit
    const res = await api.get<SegmentTopModelsResponse>('/public/segments/top-models', { params: qp })
    return res.data
  },

  // Age Analytics
  getAgeGroupsOverview: (params?: {
    start_date?: string
    end_date?: string
    mine_postnummer?: string
  }) => api.get('/public/age-groups/overview', { params }),
  
  getAgeGroupsTopModels: (params?: {
    start_date?: string
    end_date?: string
    limit?: number
    mine_postnummer?: string
  }) => api.get('/public/age-groups/top-models', { params }),
  
  getAgeGroupsTopBrands: (params?: {
    start_date?: string
    end_date?: string
    limit?: number
    mine_postnummer?: string
  }) => api.get('/public/age-groups/top-brands', { params }),
  
  getModelAgeProfile: (modelVariant: string, params?: {
    brand?: string
    start_date?: string
    end_date?: string
    mine_postnummer?: string
  }) => api.get(`/public/models/${encodeURIComponent(modelVariant)}/age-profile`, { params }),
  
  getAgeGroupsLeasingShare: (params?: {
    start_date?: string
    end_date?: string
    mine_postnummer?: string
  }) => api.get('/public/age-groups/leasing-share', { params }),

  // Teknisk – Akseldrift (#872)
  getAkseldriftOversikt: (params?: {
    output_mode?: string
    varebil?: boolean
    mine_postnummer?: string
    custom_start_date?: string
    custom_end_date?: string
  }) => api.get('/public/teknisk/akseldrift/oversikt', { params }),

  getAkseldriftTidsserie: (params?: {
    output_mode?: string
    varebil?: boolean
    mine_postnummer?: string
    custom_start_date?: string
    custom_end_date?: string
  }) => api.get('/public/teknisk/akseldrift/tidsserie', { params }),

  getAkseldriftMerker: (params?: {
    output_mode?: string
    varebil?: boolean
    mine_postnummer?: string
    custom_start_date?: string
    custom_end_date?: string
  }) => api.get('/public/teknisk/akseldrift/merker', { params }),

  getAkseldriftModeller: (params?: {
    output_mode?: string
    varebil?: boolean
    mine_postnummer?: string
    custom_start_date?: string
    custom_end_date?: string
    kun_begge?: boolean
    segment_id?: number
  }) => api.get('/public/teknisk/akseldrift/modeller', { params }),

  getAkseldriftSegmenter: (params?: {
    output_mode?: string
    varebil?: boolean
    mine_postnummer?: string
    custom_start_date?: string
    custom_end_date?: string
  }) => api.get('/public/teknisk/akseldrift/segmenter', { params }),

  getAkseldriftAlder: (params?: {
    output_mode?: string
    varebil?: boolean
    mine_postnummer?: string
    custom_start_date?: string
    custom_end_date?: string
    merke?: string
    master_model_id?: number
  }) => api.get('/public/teknisk/akseldrift/alder', { params }),

  // Teknisk – Drivstoff (#875). ekskluder_elbil leaves electric cars out of every count
  getDrivstoffOversikt: (params?: {
    output_mode?: string
    varebil?: boolean
    mine_postnummer?: string
    custom_start_date?: string
    custom_end_date?: string
    ekskluder_elbil?: boolean
  }) => api.get('/public/teknisk/drivstoff/oversikt', { params }),

  getDrivstoffTidsserie: (params?: {
    output_mode?: string
    varebil?: boolean
    mine_postnummer?: string
    custom_start_date?: string
    custom_end_date?: string
    ekskluder_elbil?: boolean
  }) => api.get('/public/teknisk/drivstoff/tidsserie', { params }),

  getDrivstoffMerker: (params?: {
    output_mode?: string
    varebil?: boolean
    mine_postnummer?: string
    custom_start_date?: string
    custom_end_date?: string
    ekskluder_elbil?: boolean
  }) => api.get('/public/teknisk/drivstoff/merker', { params }),

  getDrivstoffModeller: (params?: {
    output_mode?: string
    varebil?: boolean
    mine_postnummer?: string
    custom_start_date?: string
    custom_end_date?: string
    ekskluder_elbil?: boolean
  }) => api.get('/public/teknisk/drivstoff/modeller', { params }),

  getDrivstoffHybrid: (params?: {
    output_mode?: string
    varebil?: boolean
    mine_postnummer?: string
    custom_start_date?: string
    custom_end_date?: string
    ladbar?: string
    basis?: string
  }) => api.get('/public/teknisk/drivstoff/hybrid', { params }),

  getDrivstoffAlder: (params?: {
    output_mode?: string
    varebil?: boolean
    mine_postnummer?: string
    custom_start_date?: string
    custom_end_date?: string
    ekskluder_elbil?: boolean
    merke?: string
    master_model_id?: number
  }) => api.get('/public/teknisk/drivstoff/alder', { params }),

  // Age Analytics – Filtered (Tabs: Generelt / Leasing / Lån)
  getAgeGroupsOverviewFiltered: (params?: {
    output_mode?: string
    category?: string
    varebil?: boolean
    mine_postnummer?: string
    custom_start_date?: string
    custom_end_date?: string
  }) => api.get('/public/age-groups/overview-filtered', { params }),

  getAgeAvgByBrand: (params?: {
    output_mode?: string
    category?: string
    varebil?: boolean
    mine_postnummer?: string
    custom_start_date?: string
    custom_end_date?: string
    limit?: number
  }) => api.get('/public/age-groups/avg-age-by-brand', { params }),

  getBrandAgeDistribution: (params: {
    brand: string
    category?: string
    output_mode?: string
    start_date?: string
    end_date?: string
    compare_brand?: string
    varebil?: boolean
    mine_postnummer?: string
    custom_start_date?: string
    custom_end_date?: string
  }) => api.get('/public/age-groups/brand-distribution', { params }),

  getAgeWeeklyAvgAge: (params: {
    year: number
    varebil?: boolean
  }) => api.get('/public/age-groups/weekly-avg-age', { params }),

  getAvgAgeByMasterModel: (params?: {
    output_mode?: string
    category?: string
    varebil?: boolean
    mine_postnummer?: string
    custom_start_date?: string
    custom_end_date?: string
    search?: string
    limit?: number
    offset?: number
  }) => api.get('/public/age-groups/avg-age-by-master-model', { params }),

  getMasterModelDistribution: (params: {
    master_model_id: number
    category?: string
    output_mode?: string
    start_date?: string
    end_date?: string
    compare_master_model_id?: number
    varebil?: boolean
    mine_postnummer?: string
    custom_start_date?: string
    custom_end_date?: string
  }) => api.get('/public/age-groups/master-model-distribution', { params }),

  // Announcements (public endpoint, no API key required)
  getAnnouncements: (params?: {
    content_type?: string
    start_date?: string
    end_date?: string
    include_expired?: boolean
  }) => {
    const tempApi = axios.create({
      baseURL: API_BASE_URL,
      timeout: REQUEST_TIMEOUT_MS,
      headers: {
        'Content-Type': 'application/json',
      },
    })
    return tempApi.get('/public/announcements', { params })
  },

  // Loan Analytics APIs
  getLoanOverview: (params?: {
    start_date?: string
    end_date?: string
    mine_postnummer?: string
  }) => api.get('/public/loans/overview', { params }),

  getLoansByBrand: (params?: {
    start_date?: string
    end_date?: string
    limit?: number
    mine_postnummer?: string
    varebil?: boolean
  }) => api.get('/public/loans/by-brand', { params }),

  getLoansByModel: (params?: {
    brand?: string
    start_date?: string
    end_date?: string
    limit?: number
    mine_postnummer?: string
  }) => api.get('/public/loans/by-model', { params }),

  getLoansByVehicleAge: (params?: {
    start_date?: string
    end_date?: string
    mine_postnummer?: string
  }) => api.get('/public/loans/by-vehicle-age', { params }),

  getLoansByOwnerAge: (params?: {
    start_date?: string
    end_date?: string
    mine_postnummer?: string
  }) => api.get('/public/loans/by-owner-age', { params }),

  getLoansByBank: (params?: {
    limit?: number
  }) => api.get('/public/loans/by-bank', { params }),

  getLeasingByLeasingselskap: (params?: {
    start_date?: string
    end_date?: string
    limit?: number
    MerkeNavn?: string
    varebil?: boolean
  }) => api.get('/public/leasing/by-leasingselskap', { params }),

  // Master Model Analytics API
  getMasterModels: (params?: {
    start_date?: string
    end_date?: string
    brand_key?: string
    mine_postnummer?: string
    min_count?: number
    limit?: number
    varebil?: boolean
  }) => api.get('/public/master-models', { params }),

  // Model Variants by Master Model API
  getMasterModelVariants: (masterModelId: number, params?: {
    start_date?: string
    end_date?: string
    mine_postnummer?: string
    limit?: number
    varebil?: boolean
  }) => api.get(`/public/master-models/${masterModelId}/variants`, { params }),

  // Data Freshness API
  getDataFreshness: () => api.get('/public/data-freshness'),

  // Flat brand list for filter dropdowns (cheap; replaces getLoansByBrand for this purpose)
  getBrands: () => api.get('/public/brands'),

  // Andre kjøretøy (#982): merke/modell per non-car vehicle class
  getAndreKjoretoyMerker: (params: AndreKjoretoyParams) =>
    api.get('/public/andre-kjoretoy/merker', { params }),
  getAndreKjoretoyModeller: (params: AndreKjoretoyParams & { merke?: string }) =>
    api.get('/public/andre-kjoretoy/modeller', { params }),

  // Total Statistics (Totalstatistikk) - YoY brand comparison
  getTotalStatsBrands: (params?: {
    output_mode?: string
    varebil?: boolean
    mine_postnummer?: string
    limit?: number
    custom_start_date?: string
    custom_end_date?: string
  }) => api.get('/public/total-stats-brands', { params }),

  // Total Statistics - Master models drill-down (Level 2)
  getTotalStatsMasterModels: (params: {
    brand?: string
    output_mode?: string
    varebil?: boolean
    mine_postnummer?: string
    limit?: number
    custom_start_date?: string
    custom_end_date?: string
  }) => api.get('/public/total-stats-master-models', { params }),

  // Total Statistics - Variants drill-down (Level 3)
  getTotalStatsVariants: (params: {
    master_model_id: number
    output_mode?: string
    varebil?: boolean
    mine_postnummer?: string
    limit?: number
    custom_start_date?: string
    custom_end_date?: string
  }) => api.get('/public/total-stats-variants', { params }),

  // Dashboard Weekly Time-Series
  getWeeklyTimeSeries: (params: {
    year: number
    varebil?: boolean
  }) => api.get('/public/dashboard-weekly-timeseries', { params }),

  // Dashboard per-brand weekly counts (Mine Merker lines)
  getBrandWeeklyTimeSeries: (params: {
    year: number
    merke: string[]
    varebil?: boolean
  }) => {
    const qp = new URLSearchParams()
    qp.append('year', String(params.year))
    params.merke.forEach(m => qp.append('merke', m))
    if (params.varebil !== undefined) qp.append('varebil', String(params.varebil))
    return api.get(`/public/dashboard-brand-weekly?${qp.toString()}`)
  },

  // Perioder > Graf: per-brand registrations per ISO week or month (#685)
  getBrandPeriodTimeseries: (params: {
    start_date: string
    end_date: string
    prev_start_date?: string
    prev_end_date?: string
    merke: string[]
    granularity: 'week' | 'month'
    varebil?: boolean
    mine_postnummer?: string
  }) => {
    const qp = new URLSearchParams()
    qp.append('start_date', params.start_date)
    qp.append('end_date', params.end_date)
    if (params.prev_start_date && params.prev_end_date) {
      qp.append('prev_start_date', params.prev_start_date)
      qp.append('prev_end_date', params.prev_end_date)
    }
    params.merke.forEach(m => qp.append('merke', m))
    qp.append('granularity', params.granularity)
    if (params.varebil !== undefined) qp.append('varebil', String(params.varebil))
    if (params.mine_postnummer) qp.append('mine_postnummer', params.mine_postnummer)
    return api.get(`/public/brand-period-timeseries?${qp.toString()}`)
  },

  // Weekly loan counts (lazy-loaded for Andeler tab)
  getWeeklyLoanCounts: (params: {
    year: number
    varebil?: boolean
  }) => api.get('/public/weekly-loan-counts', { params }),

  // Organization details (Næring aktør detaljer)
  getOrgDetailsSummary: (
    orgnr: string,
    params: {
      from: string
      to: string
      varebil?: boolean
      classification?: string
      mine_postnummer?: string
    }
  ) => api.get<OrgDetailsSummary>(
    `/public/organizations/${encodeURIComponent(orgnr)}/details/summary`,
    { params }
  ),

  getOrgDetailsTimeline: (
    orgnr: string,
    params: {
      varebil?: boolean
      classification?: string
      mine_postnummer?: string
    }
  ) => api.get<{ weeks: OrgDetailsTimelineWeek[] }>(
    `/public/organizations/${encodeURIComponent(orgnr)}/details/timeline`,
    { params }
  ),

  getOrgDetailsAggregatedCars: (
    orgnr: string,
    params: {
      from: string
      to: string
      role?: OrgRole
      varebil?: boolean
      classification?: string
      mine_postnummer?: string
      page?: number
      page_size?: number
      sort?: "antall" | "reg_date" | "brand" | "master_model"
      order?: "asc" | "desc"
    }
  ) => api.get<OrgDetailsAggregatedResponse>(
    `/public/organizations/${encodeURIComponent(orgnr)}/details/cars-aggregated`,
    { params }
  ),

  // Subscription request (public, no auth)
  submitSubscriptionRequest: (data: {
    plan_id: string
    plan_name: string
    plan_price: string
    org_number: string
    org_name: string
    contact_name: string
    contact_email: string
    contact_phone?: string
    message?: string
    accepted_terms: boolean
  }) => {
    const tempApi = axios.create({
      baseURL: API_BASE_URL,
      timeout: REQUEST_TIMEOUT_MS,
      headers: { 'Content-Type': 'application/json' },
    })
    return tempApi.post('/public/subscription-request', data)
  },
}

// Private Endpoints (requires API key with external scope)
export const privateAPI = {
  // Age group analytics
  getAgeGroups: (params?: {
    start_date?: string
    end_date?: string
    age_brackets?: number
  }) => api.get('/private/age-groups', { params }),

  // Brand-specific age analytics
  getBrandAgeGroups: (brand: string, params?: {
    start_date?: string
    end_date?: string
    age_brackets?: number
  }) => api.get(`/private/brands/${encodeURIComponent(brand)}/age-groups`, { params }),

  // Model analytics within a brand
  getBrandModels: (brand: string, params?: {
    start_date?: string
    end_date?: string
    limit?: number
  }) => api.get(`/private/brands/${encodeURIComponent(brand)}/models`, { params }),

  // Leasing overview
  getLeasingOverview: (params?: {
    start_date?: string
    end_date?: string
  }) => api.get('/private/leasing/overview', { params }),

  // Leasing by brand
  getLeasingByBrand: (params?: {
    start_date?: string
    end_date?: string
    limit?: number
    mine_postnummer?: string
    varebil?: boolean
  }) => api.get('/private/leasing/by-brand', { params }),

  // Leasing by master model
  getLeasingByMasterModel: (params?: {
    start_date?: string
    end_date?: string
    limit?: number
    brand_key?: string
    mine_postnummer?: string
  }) => api.get('/private/leasing/by-master-model', { params }),

  // Company sales analytics
  getCompanySales: (params?: {
    start_date?: string
    end_date?: string
    limit?: number
  }) => api.get('/private/company-sales', { params }),

  // Company car analytics by brand
  getCompanyCarsByBrand: (params?: {
    start_date?: string
    end_date?: string
    limit?: number
    mine_postnummer?: string
  }) => api.get('/private/company-cars/by-brand', { params }),

  // Company car analytics by master model
  getCompanyCarsByMasterModel: (params?: {
    start_date?: string
    end_date?: string
    limit?: number
    brand_key?: string
    mine_postnummer?: string
  }) => api.get('/private/company-cars/by-master-model', { params }),

  // Brand buyers - orgs that purchase a given brand (owned, not leased)
  getBrandBuyers: (params: {
    brand: string
    start_date: string
    end_date: string
    mine_postnummer?: string
    varebil?: boolean
    classification?: string
    page?: number
    page_size?: number
  }) => api.get('/public/organizations/brand-buyers', { params }),

  // Brand leasers - orgs that lease a given brand (næringsleasing)
  getBrandLeasers: (params: {
    brand: string
    start_date: string
    end_date: string
    mine_postnummer?: string
    varebil?: boolean
    classification?: string
    page?: number
    page_size?: number
  }) => api.get('/public/organizations/brand-leasers', { params }),

  // Brand combined - owned (Eier) + leased (Leasingtaker) per org for a given brand (#423)
  getBrandCombined: (params: {
    brand: string
    start_date: string
    end_date: string
    mine_postnummer?: string
    varebil?: boolean
    classification?: string
    page?: number
    page_size?: number
  }) => api.get('/public/organizations/brand-combined', { params }),
}

// Bruktbilfinansiering API
export const bruktbilfinansieringAPI = {
  getOverview: (bankOrgNr: string, params: {
    start_date: string; end_date: string; dealer_postnummer?: string; buyer_postnummer?: string; varebil?: boolean; classes?: string[]
  }) => api.get(`/bruktbilfinansiering/${encodeURIComponent(bankOrgNr)}/overview`, { params }),

  getDealers: (bankOrgNr: string, params: {
    start_date: string; end_date: string; dealer_postnummer?: string; buyer_postnummer?: string;
    varebil?: boolean;
    sort_by?: string; sort_dir?: string; classes?: string[]
  }) => api.get(`/bruktbilfinansiering/${encodeURIComponent(bankOrgNr)}/dealers`, { params }),

  getDatakvalitet: (bankOrgNr: string, params: {
    start_date: string; end_date: string; dealer_postnummer?: string; buyer_postnummer?: string; varebil?: boolean; classes?: string[]
  }) => api.get(`/bruktbilfinansiering/${encodeURIComponent(bankOrgNr)}/datakvalitet`, { params }),

  // Freshness block only (bilpriserproff#281). Takes no date params — every
  // field is a global maximum. Use this for the "Datasett oppdatert" pill;
  // getDatakvalitet costs ~73 s for five further blocks the pill discards.
  getFreshness: (bankOrgNr: string) =>
    api.get(`/bruktbilfinansiering/${encodeURIComponent(bankOrgNr)}/freshness`),

  getDealersCrossBank: (bankOrgNr: string, params: {
    start_date?: string; end_date?: string; dealer_postnummer?: string; buyer_postnummer?: string; varebil?: boolean; classes?: string[]
  }) => api.get(`/bruktbilfinansiering/${encodeURIComponent(bankOrgNr)}/dealers-cross-bank`, { params }),

  getDealerDetails: (bankOrgNr: string, dealerOrgNr: string, params: {
    start_date?: string; end_date?: string; varebil?: boolean; classes?: string[];
    dealer_postnummer?: string; buyer_postnummer?: string; buyer_fylke?: string
  }) => api.get(
    `/bruktbilfinansiering/${encodeURIComponent(bankOrgNr)}/dealers/${encodeURIComponent(dealerOrgNr)}/details`,
    { params }
  ),

  // Solgte biler without loan data (#407). Same dealer population as
  // getDealerDetails' buyer_rows, but the backend runs it to
  // MAX(hendelse_tidspunkt) instead of stopping at the 15-day Brreg lag.
  getDealerSoldRows: (bankOrgNr: string, dealerOrgNr: string, params: {
    start_date?: string; end_date?: string; varebil?: boolean; classes?: string[];
    dealer_postnummer?: string; buyer_postnummer?: string; buyer_fylke?: string
  }) => api.get(
    `/bruktbilfinansiering/${encodeURIComponent(bankOrgNr)}/dealers/${encodeURIComponent(dealerOrgNr)}/sold-rows`,
    { params }
  ),

  getMyBanks: () => api.get('/bruktbilfinansiering/banks'),

  getTransferClasses: (bankOrgNr: string, params: {
    start_date: string; end_date: string; dealer_postnummer?: string; buyer_postnummer?: string; varebil?: boolean; classes?: string[]
  }) => api.get(`/bruktbilfinansiering/${encodeURIComponent(bankOrgNr)}/transfer-classes`, { params }),

  getClassDetails: (bankOrgNr: string, classLabel: string, params: {
    start_date: string; end_date: string; buyer_postnummer?: string; dealer_postnummer?: string; varebil?: boolean
  }) => api.get(
    `/bruktbilfinansiering/${encodeURIComponent(bankOrgNr)}/transfer-classes/${encodeURIComponent(classLabel)}/details`,
    { params }
  ),

  getLoanAmountProfile: (bankOrgNr: string, params: {
    start_date: string; end_date: string;
    classes?: string[];
    compare_bank_org_nrs?: string[];
    varebil?: boolean;
    dealer_postnummer?: string;
    buyer_postnummer?: string;
  }) => api.get(`/bruktbilfinansiering/${encodeURIComponent(bankOrgNr)}/loan-amount-profile`, { params }),

  getBorrowerAgeProfile: (bankOrgNr: string, params: {
    start_date: string; end_date: string;
    classes?: string[];
    compare_bank_org_nrs?: string[];
    varebil?: boolean;
    dealer_postnummer?: string;
    buyer_postnummer?: string;
  }) => api.get(`/bruktbilfinansiering/${encodeURIComponent(bankOrgNr)}/borrower-age-profile`, { params }),

  getCarAgeBuckets: (bankOrgNr: string, params: {
    start_date: string; end_date: string;
    dealer_postnummer?: string;
    buyer_postnummer?: string;
    varebil?: boolean;
    classes?: string[];
    compare_bank_org_nrs?: string[];
  }) => api.get(`/bruktbilfinansiering/${encodeURIComponent(bankOrgNr)}/car-age-buckets`, { params }),

  getBankMarket: (bankOrgNr: string, params: {
    start_date: string
    end_date: string
    dealer_postnummer?: string
    buyer_postnummer?: string
    varebil?: boolean
    merke?: string[]
    classes?: string[]
  }) => {
    const p = new URLSearchParams()
    p.set('start_date', params.start_date)
    p.set('end_date', params.end_date)
    if (params.dealer_postnummer) p.set('dealer_postnummer', params.dealer_postnummer)
    if (params.buyer_postnummer) p.set('buyer_postnummer', params.buyer_postnummer)
    if (params.varebil !== undefined) p.set('varebil', String(params.varebil))
    params.merke?.forEach(m => p.append('merke', m))
    params.classes?.forEach(c => p.append('classes', c))
    return api.get(`/bruktbilfinansiering/${encodeURIComponent(bankOrgNr)}/bank-market?${p.toString()}`)
  },

  getHendelser: (bankOrgNr: string, params: {
    start_date: string
    end_date: string
    type?: string
    source?: string
    dealer_org_nr?: string
    page?: number
    page_size?: number
  }) => api.get(`/bruktbilfinansiering/${encodeURIComponent(bankOrgNr)}/hendelser`, { params }),

  getHendelserForhandlere: (bankOrgNr: string) =>
    api.get(`/bruktbilfinansiering/${encodeURIComponent(bankOrgNr)}/hendelser/forhandlere`),
}

// Mine Markedsområder
export interface Markedsomrade {
  id: number
  name: string
  postnumre: string[]
  sort_order: number
}

export interface MarkedsomradeListResponse {
  items: Markedsomrade[]
}

// Mine Merker
export interface MineMerke {
  id: number
  merke: string
  merke_navn: string
  /** Display name per backend brand_display.py (DEEPAL -> CHANGAN); absent from older backends */
  visningsnavn?: string
  is_active: boolean
  sort_order: number
}

export interface MineMerkeListResponse {
  items: MineMerke[]
}

export interface AvailableMerke {
  merke: string
  merke_navn: string
}

export interface AvailableMerkerResponse {
  items: AvailableMerke[]
}

// User Profile Management
export const userAPI = {
  // Get complete user profile (profile + API key info)
  getProfile: () => api.get('/user/profile'),

  // Get only postal codes
  getPostnummer: () => api.get('/user/profile/postnummer'),

  // Update postal codes
  updatePostnummer: (mine_postnummer: string) => 
    api.put('/user/profile/postnummer', { mine_postnummer }),

  // Delete postal codes
  deletePostnummer: () => api.delete('/user/profile/postnummer'),

  // Get current user's saved organisasjonsnummer list
  getMineOrganisasjoner: () => api.get('/me/organisasjoner'),

  // Replace current user's saved organisasjonsnummer list
  updateMineOrganisasjoner: (org_numbers: string[]) =>
    api.put('/me/organisasjoner', { org_numbers }),

  // List user's saved markedsområder
  getMineMarkedsomrader: () =>
    api.get<MarkedsomradeListResponse>('/me/markedsomrader'),

  // Create a new markedsområde
  createMarkedsomrade: (name: string, postnumre: string[]) =>
    api.post<Markedsomrade>('/me/markedsomrader', { name, postnumre }),

  // Update an existing markedsområde
  updateMarkedsomrade: (id: number, name: string, postnumre: string[]) =>
    api.put<Markedsomrade>(`/me/markedsomrader/${id}`, { name, postnumre }),

  // Delete a markedsområde
  deleteMarkedsomrade: (id: number) =>
    api.delete<void>(`/me/markedsomrader/${id}`),

  // List user's saved merker
  getMineMerker: () =>
    api.get<MineMerkeListResponse>('/me/merker'),

  // Add a brand to favourites
  createMineMerke: (merke: string, merke_navn: string) =>
    api.post<MineMerke>('/me/merker', { merke, merke_navn }),

  // Toggle is_active
  toggleMineMerke: (id: number, is_active: boolean) =>
    api.patch<MineMerke>(`/me/merker/${id}`, { is_active }),

  // Remove a brand
  deleteMineMerke: (id: number) =>
    api.delete<void>(`/me/merker/${id}`),

  // Get available brands (DISTINCT from SVVFirstTimeReg, excluding saved)
  getAvailableMerker: () =>
    api.get<AvailableMerkerResponse>('/me/merker/available'),

  // Submit feedback/bug report/change request
  submitFeedback: (formData: FormData) =>
    api.post('/user/feedback', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
}

// Hendelsessenter (event center) for Mine Organisasjoner
export type HendelseType = 'eie' | 'leasing' | 'solgt' | 'kjopt'
export type HendelseSource = 'nyreg' | 'bruktbil'

export interface HendelsessenterSummary {
  total_cars: number
  total_orgs: number
  eie_count: number
  leasing_count: number
  solgt_count: number
  kjopt_count: number
}

export interface HendelsessenterPerOrg {
  org_nr: string
  org_navn: string | null
  total: number
  eie: number
  leasing: number
  solgt: number
  kjopt: number
}

export interface HendelsessenterRow {
  merke: string
  modell: string
  registrert: string
  postnummer: string
  poststed: string
  type: HendelseType
  org_nr: string
  org_navn: string | null
  source: HendelseSource
  hendelse_tidspunkt: string | null
}

export interface HendelsessenterPagination {
  page: number
  page_size: number
  total: number
  total_pages: number
}

export interface HendelsessenterResponse {
  summary: HendelsessenterSummary
  per_org: HendelsessenterPerOrg[]
  rows: HendelsessenterRow[]
  pagination: HendelsessenterPagination
}

export const hendelsessenterAPI = {
  getHendelsessenter: (params: {
    from: string
    to: string
    org?: string
    type?: HendelseType
    page?: number
    page_size?: number
  }) => api.get<HendelsessenterResponse>('/hendelsessenter', { params }),
}

// Notifications (bell)
export interface BellAckItem {
  org_nr: string
  dag: string
  type: 'eie' | 'leasing' | 'solgt' | 'kjopt'
}

export const notificationAPI = {
  // Get bell payload: { unread_count, items: [{ org_nr, org_navn, type, dag, antall, unread }] }
  getBell: () => api.get('/notifications/bell'),

  // Acknowledge a set of bell items so they stop counting as unread.
  // Persists in user_hendelse_ack; idempotent.
  ack: (items: BellAckItem[]) => api.post('/notifications/ack', { items }),

  // Legacy no-op kept for backwards compatibility with older builds.
  markSeen: () => api.post('/notifications/mark-seen'),
}

// Login/Authentication
export const loginAPI = {
  // Login with username and API key
  login: async (userName: string, apiKey: string) => {
    const tempApi = axios.create({
      baseURL: API_BASE_URL,
      timeout: REQUEST_TIMEOUT_MS,
      headers: {
        'Content-Type': 'application/json',
      },
    })
    
    // Use POST /auth/login with username and API key
    const response = await tempApi.post('/auth/login', { 
      user_name: userName,
      api_key: apiKey 
    })
    return response.data
  },

  // Test if API key is valid using the auth/validate-key endpoint
  testApiKey: async (apiKey: string) => {
    const tempApi = axios.create({
      baseURL: API_BASE_URL,
      timeout: REQUEST_TIMEOUT_MS,
      headers: {
        'Content-Type': 'application/json',
      },
    })
    
    // Validate API key using POST /auth/validate-key
    const response = await tempApi.post('/auth/validate-key', { api_key: apiKey })
    return response.data
  },
}

// Leasing Campaigns API (uses same base URL as main API)
const campaignsApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: REQUEST_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
})

// Request interceptor for campaigns API - use JWT token
campaignsApi.interceptors.request.use(
  (config) => {
    const accessToken = authService.getAccessToken()
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor — same refresh-and-retry logic as main api
campaignsApi.interceptors.response.use(
  (response) => response,
  createResponseErrorHandler(authService, (cfg) => campaignsApi(cfg)),
)

export const leasingCampaignsAPI = {
  // Get leasing campaign results
  getResults: (params?: {
    skip?: number
    limit?: number
    active_only?: boolean
  }) => campaignsApi.get('/public/results/', { params }),
  
  // Get campaign history
  getCampaignHistory: (campaignId: string) => 
    campaignsApi.get(`/public/results/campaign-history/${campaignId}`),
}

export default api
